/**
 * Breadcrumb do header: onde a pessoa está, derivado do menu lateral (seção)
 * e do que a tela já declara (`PageTitle`, abas de `HeaderNavigations`). A
 * tela que precisa de nível de entidade passa os níveis em
 * `PageTitle breadcrumbs`.
 */
import {PageLink} from '../../core/PageData';
import {AppMenuItem} from '../aside/appMenu';
import {normalizeText} from './text';

export type Crumb = {
  /** Vazio enquanto o nome carrega. */
  title: string;
  /** Só os níveis anteriores têm link. */
  path?: string;
  loading?: boolean;
};

export type TrailInput = {
  pathname: string;
  menu: AppMenuItem[];
  pageTitle?: string;
  pageTitleLoading?: boolean;
  pageBreadcrumbs?: PageLink[];
  pageHeaderNavigation?: PageLink[];
};

const sameText = (a?: string, b?: string) =>
  Boolean(a && b) && normalizeText(a as string) === normalizeText(b as string);

const pathOnly = (path = '') => path.split(/[?#]/)[0];

const firstSegment = (path: string) => pathOnly(path).split('/')[1] || '';

/** A aba é a página atual ou uma página dentro dela (`/finance/invoices/:uid`). */
export const isTabActive = (
  pathname: string,
  tabPath: string,
  alsoActiveOn: string[] = []
) => {
  const current = pathOnly(pathname).replace(/\/$/, '');
  const matches = (path: string) => {
    const tab = pathOnly(path).replace(/\/$/, '');
    return Boolean(tab) && (current === tab || current.startsWith(`${tab}/`));
  };
  return matches(tabPath) || alsoActiveOn.some(matches);
};

/**
 * Item do menu que é a seção da página: o próprio item; o item que é uma das
 * abas da página; ou o item do mesmo módulo (primeiro segmento da URL),
 * preferindo o de mesmo nome do título quando há mais de um (Cotações).
 */
export const resolveSection = (
  pathname: string,
  menu: AppMenuItem[],
  tabs: PageLink[] = [],
  pageTitle?: string
): AppMenuItem | undefined => {
  const current = pathOnly(pathname);
  const exact = menu.find(({to}) => to === current);
  if (exact) return exact;

  const tabPaths = tabs.map(({path}) => pathOnly(path));
  const inTabs = menu.find(({to}) => tabPaths.includes(to));
  if (inTabs) return inTabs;

  const segment = firstSegment(current);
  if (!segment) return undefined;
  const sameModule = menu.filter(({to}) => firstSegment(to) === segment);
  return (
    sameModule.find(({title}) => sameText(title, pageTitle)) || sameModule[0]
  );
};

/**
 * Níveis do breadcrumb, do mais geral ao atual. O último é a página (sem
 * link); os anteriores levam aos seus caminhos.
 */
export const buildTrail = ({
  pathname,
  menu,
  pageTitle = '',
  pageTitleLoading = false,
  pageBreadcrumbs = [],
  pageHeaderNavigation = [],
}: TrailInput): Crumb[] => {
  const title = pageTitle.trim();
  const section = resolveSection(pathname, menu, pageHeaderNavigation, title);
  const explicit = pageBreadcrumbs
    .filter(({isSeparator}) => !isSeparator)
    .map(({title: crumbTitle, path}) => ({
      title: (crumbTitle || '').trim(),
      path,
      loading: !(crumbTitle || '').trim(),
    }));

  const levels: Crumb[] = [];
  if (explicit.length) {
    // A tela diz os níveis; a seção entra na frente se ela não a repetiu
    const hasSection =
      section &&
      explicit.some(
        ({title: crumbTitle, path}) =>
          pathOnly(path) === section.to || sameText(crumbTitle, section.title)
      );
    if (section && !hasSection) {
      levels.push({title: section.title, path: section.to});
    }
    levels.push(...explicit);
    if (pageTitleLoading) {
      levels.push({title: '', loading: true});
    } else if (title && !sameText(title, levels[levels.length - 1]?.title)) {
      levels.push({title});
    }
  } else {
    // Seção do menu + a página: a aba ativa ou o título que a tela já define
    const activeTab = pageHeaderNavigation.find(
      ({path, isSeparator, alsoActiveOn}) =>
        !isSeparator && isTabActive(pathname, path, alsoActiveOn)
    );
    const page = activeTab?.title.trim() || title;
    if (section) levels.push({title: section.title, path: section.to});
    if (pageTitleLoading) {
      levels.push({title: '', loading: true});
    } else if (page && !sameText(page, section?.title)) {
      levels.push({title: page});
    }
  }

  // O último nível é onde a pessoa está: sem link
  return levels.map((level, index) =>
    index === levels.length - 1 ? {...level, path: undefined} : level
  );
};
